import { NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';

import { SCHEMA_STATE } from '../address/schema-state';
import { DataState } from '../model/data-state';
import { SchemaRepeatedNodeRef } from './schema-repeated-node';

/**
 * One row of a repeated node: what `kros-schema-node-outlet` is for a node, without what a row does not have.
 *
 * A repeater resolves its row node once with `injectSchemaRepeatedNode()` and renders each value through this outlet,
 * which renders the row node's component with the config every row shares and the row's own state. It also makes the
 * row a place of its own: it provides `SCHEMA_STATE`, so an action fired from the row reads the row as `$data` (D11) —
 * what the row's component reads through `this.schema` — rather than the list the row sits in. The row's source stays
 * the list's: it is where the row came from.
 *
 * None of the node outlet's gate and scope: a repeated node has no `showIf`, `dataSource`, `query` or `states`, and the
 * repeater provides the one `SchemaActionsHost` every row shares. The outlet lives as long as its row's view, so a
 * refreshed row reaches the component through `state` rather than by creating it anew, and its host is
 * `display: contents`, as the node outlet's is, so the component lays out as if the repeater rendered it directly.
 */
@Component({
  selector: 'kros-schema-row-outlet',
  imports: [NgComponentOutlet],
  template: `
    @if (item().component(); as component) {
      <ng-container *ngComponentOutlet="component; inputs: { config: item().config(), state: state() }" />
    }
  `,
  styles: `
    :host {
      display: contents;
    }
  `,
  providers: [{ provide: SCHEMA_STATE, useFactory: () => inject(SchemaRowOutletComponent).state }],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SchemaRowOutletComponent {
  readonly item = input.required<SchemaRepeatedNodeRef>();

  readonly state = input<DataState>();
}
